import { Fragment, type MouseEvent } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import type { SupportedLocale } from '../../lib/i18n'
import type { AdminBookingSummary } from '../../graphql/admin'
import BookingStatusBadge from '../customer/BookingStatusBadge'
import Button from '../ui/Button'
import {
  formatAdminCurrency,
  formatAdminDate,
  formatAdminDateTime,
  formatAdminServiceType,
  formatAdminTimeRange,
  formatAdminUserName,
} from './formatters'

interface BookingTableProps {
  bookings: AdminBookingSummary[]
  locale: SupportedLocale
  emptyMessage: string
  expandedBookingId: string | null
  cancellingBookingId: string | null
  onToggleExpand: (bookingId: string) => void
  onCancelBooking: (booking: AdminBookingSummary) => void
}

function isCancellable(status: string) {
  return [ 'pending', 'confirmed', 'in_progress' ].includes(status)
}

export default function BookingTable({
  bookings,
  locale,
  emptyMessage,
  expandedBookingId,
  cancellingBookingId,
  onToggleExpand,
  onCancelBooking,
}: BookingTableProps) {
  function stop(event: MouseEvent) {
    event.stopPropagation()
  }

  if (bookings.length === 0) {
    return (
      <div className="rounded-[1.5rem] border border-dashed border-slate-300 bg-white p-8 text-sm text-slate-500">
        {emptyMessage}
      </div>
    )
  }

  return (
    <div className="overflow-x-auto rounded-[1.5rem] border border-slate-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-slate-200">
        <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
          <tr>
            <th className="px-5 py-4">ID</th>
            <th className="px-5 py-4">Customer</th>
            <th className="px-5 py-4">Provider</th>
            <th className="px-5 py-4">Service</th>
            <th className="px-5 py-4">Date</th>
            <th className="px-5 py-4">Status</th>
            <th className="px-5 py-4">Price</th>
            <th className="px-5 py-4 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {bookings.map((booking) => {
            const expanded = expandedBookingId === booking.id

            return (
              <Fragment key={booking.id}>
                <tr
                  onClick={() => onToggleExpand(booking.id)}
                  className="cursor-pointer transition hover:bg-slate-50"
                >
                  <td className="px-5 py-4 text-sm font-semibold text-slate-900">#{booking.id}</td>
                  <td className="px-5 py-4 text-sm text-slate-700">{formatAdminUserName(booking.customer)}</td>
                  <td className="px-5 py-4 text-sm text-slate-700">{formatAdminUserName(booking.providerProfile.user)}</td>
                  <td className="px-5 py-4 text-sm text-slate-700">{formatAdminServiceType(booking.serviceType, locale)}</td>
                  <td className="px-5 py-4 text-sm text-slate-700">{formatAdminDate(booking.scheduledDate, locale)}</td>
                  <td className="px-5 py-4">
                    <BookingStatusBadge status={booking.status} />
                  </td>
                  <td className="px-5 py-4 text-sm font-semibold text-slate-900">{formatAdminCurrency(booking.priceCents, locale)}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-2" onClick={stop}>
                      {isCancellable(booking.status) ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="danger"
                          loading={cancellingBookingId === booking.id}
                          onClick={() => onCancelBooking(booking)}
                        >
                          Cancel
                        </Button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => onToggleExpand(booking.id)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-100"
                        aria-label={expanded ? 'Hide details' : 'Show details'}
                      >
                        {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                    </div>
                  </td>
                </tr>
                {expanded ? (
                  <tr className="bg-slate-50">
                    <td colSpan={8} className="px-5 py-5">
                      <div className="grid gap-4 text-sm text-slate-600 md:grid-cols-2 xl:grid-cols-4">
                        <div>
                          <dt className="font-semibold text-slate-900">Schedule</dt>
                          <dd className="mt-1">
                            {formatAdminDate(booking.scheduledDate, locale)} · {formatAdminTimeRange(booking.startTime, booking.endTime)}
                          </dd>
                        </div>
                        <div>
                          <dt className="font-semibold text-slate-900">Created</dt>
                          <dd className="mt-1">{formatAdminDateTime(booking.createdAt, locale)}</dd>
                        </div>
                        <div>
                          <dt className="font-semibold text-slate-900">Customer email</dt>
                          <dd className="mt-1">{booking.customer.email}</dd>
                        </div>
                        <div>
                          <dt className="font-semibold text-slate-900">Provider email</dt>
                          <dd className="mt-1">{booking.providerProfile.user.email}</dd>
                        </div>
                        {booking.cancellationReason ? (
                          <div className="md:col-span-2 xl:col-span-4">
                            <dt className="font-semibold text-slate-900">Cancellation</dt>
                            <dd className="mt-1">
                              {booking.cancelledBy ? `${booking.cancelledBy}: ` : ''}
                              {booking.cancellationReason}
                            </dd>
                          </div>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
